<script setup>
import { computed } from 'vue'
import { stringToColor, getInitials } from '@/lib/utils'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'

const props = defineProps({
  currentUser: {
    type: Boolean,
    default: false
  },
  entityName: {
    type: String,
    default: null
  },
  entityId: {
    type: Number,
    default: null
  },
  suffixTooltip: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: '72px'
  }
})

const webSiteStore = useWebSiteStore()
const i18n = getI18n()

const getUserIdLockByEntityId = (entityName, entityId) => {
  return webSiteStore.entityLocks[`${entityName}.${entityId}`]?.user_id
}

const displayedUserInfo = computed(() => {
  if (props.currentUser) {
    const fullData = webSiteStore.userDetails?.find(u => u.user_id === webSiteStore.user?.id)
    if (fullData) {
      return fullData
    } else {
      return {
        user_id: webSiteStore.user.id,
        name: webSiteStore.user.name,
        name_ext: webSiteStore.user.surname,
        avatar_url: webSiteStore.user.avatar
      }
    }
  } else {
    const userId = getUserIdLockByEntityId(props.entityName, props.entityId)
    return webSiteStore.userDetails.find(u => u.user_id === userId) || {
      user_id: userId,
      name: i18n.t('common.unknown'),
      name_ext: userId,
      avatar_url: ''
    }
  }
})

const userAvatarImage = computed(() => {
  if (displayedUserInfo.value && displayedUserInfo.value.avatar_url) {
    return `data:image/png;base64,${displayedUserInfo.value.avatar_url}`
  }
  return ''
})

const avatarName = computed(() => {
  return `${displayedUserInfo.value.name} ${displayedUserInfo.value.name_ext || ''}`
})

const userAvatarInitials = computed(() => getInitials(avatarName.value))

const userAvatarStyle = computed(() => ({
  backgroundColor: stringToColor(userAvatarInitials.value)
}))

const tooltipText = computed(() => {
  if (!props.suffixTooltip) {
    return avatarName.value
  }
  return `${avatarName.value}, ${props.suffixTooltip}`
})

</script>

<template>
  <div>
    <q-avatar
      v-if="userAvatarImage"
      :size="props.size"
    >
      <img :src="userAvatarImage">
    </q-avatar>
    <q-avatar
      v-else
      :style="userAvatarStyle"
      :size="props.size"
    >
      <q-tooltip v-if="!props.currentUser">
        {{ tooltipText }}
      </q-tooltip>
      {{ userAvatarInitials }}
    </q-avatar>
  </div>
</template>

<style scoped>
</style>
